import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/lib/roles";
import {
  Plus, Trash, X, ArrowRight, CheckCircle, XCircle,
  Buildings, Receipt, Envelope, IdentificationCard,
} from "@phosphor-icons/react";

const empty = {
  name: "",
  cif: "",
  direccion_fiscal: "",
  email: "",
  telefono: "",
  aseguradoras_ids: [],
  active: true,
};

export default function Gabinetes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = can.manageInsurers(user);
  const [items, setItems] = useState([]);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");

  const load = async () => {
    const [g, a] = await Promise.all([
      api.get("/gabinetes"),
      api.get("/aseguradoras"),
    ]);
    setItems(g.data);
    setAseguradoras((a.data || []).filter((x) => x.active !== false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setErr("");
    try {
      await api.post("/gabinetes", form);
      load();
      setOpen(false);
      setForm(empty);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error");
    }
  };

  const del = async (e, g) => {
    e.stopPropagation();
    if (!confirm(`¿Eliminar el gabinete "${g.name}"?`)) return;
    try {
      await api.delete(`/gabinetes/${g.id}`);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const aseguradorasMap = useMemo(() => {
    const m = new Map();
    for (const a of aseguradoras) m.set(a.id, a);
    return m;
  }, [aseguradoras]);

  const toggleAseguradora = (id) => {
    const has = form.aseguradoras_ids.includes(id);
    setForm({
      ...form,
      aseguradoras_ids: has
        ? form.aseguradoras_ids.filter((x) => x !== id)
        : [...form.aseguradoras_ids, id],
    });
  };

  return (
    <div data-testid="gabinetes-page">
      <header className="h-14 border-b border-border bg-white px-6 flex items-center justify-between sticky top-0 z-20">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted">{canEdit ? "Administración" : "Consulta"}</div>
          <h1 className="font-display font-bold text-lg tracking-tight">Gabinetes · {items.length}</h1>
        </div>
        {canEdit && (
          <button
            onClick={() => setOpen(true)}
            data-testid="new-gabinete-button"
            className="bg-foreground text-white text-xs px-3 py-1.5 rounded-md font-medium inline-flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" weight="bold" /> Nuevo gabinete
          </button>
        )}
      </header>

      <div className="p-6 space-y-6 max-w-5xl">
        <p className="text-xs text-muted -mb-2">
          Intermediarios que tramitan expedientes en nombre de una o varias aseguradoras. Se usan para sacar la facturación periódica.
        </p>

        {items.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-border rounded-md text-muted text-sm">
            No hay gabinetes todavía.{canEdit && " Pulsa "}
            {canEdit && (
              <button onClick={() => setOpen(true)} className="font-semibold text-foreground underline">
                Nuevo gabinete
              </button>
            )}
            {canEdit && " para crear el primero."}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map((g) => (
              <button
                key={g.id}
                onClick={() => navigate(`/admin/gabinetes/${g.id}`)}
                data-testid={`gab-card-${g.id}`}
                className="text-left border border-border rounded-md bg-white p-4 transition-all relative group cursor-pointer hover:border-foreground hover:shadow-sm hover:-translate-y-0.5 hover:bg-surface/50"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Buildings className="w-4 h-4 text-violet-600 flex-shrink-0" weight="duotone" />
                      <div className="font-display font-bold text-sm tracking-tight truncate">{g.name}</div>
                    </div>
                    {g.cif && (
                      <div className="font-mono text-[11px] text-muted">CIF · {g.cif}</div>
                    )}
                    {g.email && (
                      <div className="text-[11px] text-muted mt-0.5 inline-flex items-center gap-1 truncate">
                        <Envelope className="w-3 h-3" weight="bold" /> {g.email}
                      </div>
                    )}
                  </div>
                  {g.active ? (
                    <CheckCircle className="w-3.5 h-3.5 text-status-success-fg flex-shrink-0" weight="fill" title="Activo" />
                  ) : (
                    <XCircle className="w-3.5 h-3.5 text-muted flex-shrink-0" weight="fill" title="Inactivo" />
                  )}
                </div>
                <div className="flex flex-wrap gap-1 mt-3">
                  {(g.aseguradoras_ids || []).slice(0, 5).map((aid) => {
                    const a = aseguradorasMap.get(aid);
                    if (!a) return null;
                    return (
                      <span key={aid} className="text-[10px] font-mono uppercase bg-slate-100 text-slate-700 border border-slate-200 px-1.5 py-0.5 rounded">
                        {a.code || a.name}
                      </span>
                    );
                  })}
                  {(g.aseguradoras_ids || []).length > 5 && (
                    <span className="text-[10px] text-muted">+{(g.aseguradoras_ids || []).length - 5}</span>
                  )}
                  {(g.aseguradoras_ids || []).length === 0 && (
                    <span className="text-[10px] text-amber-700 italic">Sin aseguradoras vinculadas</span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[11px] text-muted group-hover:text-foreground transition-colors inline-flex items-center gap-1">
                    Ver detalle · <Receipt className="w-3 h-3" weight="bold" /> Facturación
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" weight="bold" />
                  </span>
                  {canEdit && (
                    <button
                      onClick={(e) => del(e, g)}
                      data-testid={`del-gab-${g.id}`}
                      className="opacity-0 group-hover:opacity-100 p-1 text-muted hover:text-status-error-fg"
                    >
                      <Trash className="w-3 h-3" weight="bold" />
                    </button>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-lg w-full max-w-lg p-6 shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg tracking-tight">Nuevo gabinete</h2>
              <button onClick={() => setOpen(false)} className="text-muted hover:text-foreground"><X className="w-4 h-4" weight="bold" /></button>
            </div>
            <div className="space-y-4">
              <Field label="Nombre completo" required>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="gab-name"
                  placeholder="Ej: Pericial Intermediación SLU"
                  className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="CIF" icon={IdentificationCard}>
                  <input
                    value={form.cif}
                    onChange={(e) => setForm({ ...form, cif: e.target.value.toUpperCase() })}
                    data-testid="gab-cif"
                    placeholder="B12345678"
                    className="w-full border border-border rounded-md px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
                  />
                </Field>
                <Field label="Email" icon={Envelope}>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="gab-email"
                    placeholder="contacto@gabinete.com"
                    className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
                  />
                </Field>
              </div>
              <Field label="Teléfono" icon={IdentificationCard}>
                <input
                  type="tel"
                  value={form.telefono}
                  onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  data-testid="gab-telefono"
                  placeholder="Ej. 911 234 567"
                  className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
                />
              </Field>
              <Field label="Dirección fiscal">
                <input
                  value={form.direccion_fiscal}
                  onChange={(e) => setForm({ ...form, direccion_fiscal: e.target.value })}
                  data-testid="gab-direccion"
                  placeholder="C/ Ejemplo 12, 28013 Madrid"
                  className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
                />
              </Field>
              <Field label={`Aseguradoras vinculadas (${form.aseguradoras_ids.length})`}>
                <div className="border border-border rounded-md max-h-48 overflow-y-auto bg-white">
                  {aseguradoras.length === 0 ? (
                    <div className="p-3 text-xs text-muted text-center">No hay aseguradoras</div>
                  ) : (
                    aseguradoras.map((a) => (
                      <label key={a.id} className="flex items-center gap-2 px-3 py-1.5 text-sm border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50">
                        <input
                          type="checkbox"
                          checked={form.aseguradoras_ids.includes(a.id)}
                          onChange={() => toggleAseguradora(a.id)}
                          data-testid={`gab-aseg-${a.code}`}
                          className="rounded border-border"
                        />
                        <span className="flex-1">{a.name}</span>
                        <span className="font-mono text-[11px] text-muted">{a.code}</span>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-[11px] text-muted mt-1">Selecciona todas las compañías que el gabinete tramita.</p>
              </Field>
              {err && <div className="text-xs text-status-error-fg bg-status-error-bg border border-status-error-bd px-2 py-1.5 rounded">{err}</div>}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="text-xs px-4 py-2 text-muted hover:text-foreground">Cancelar</button>
              <button
                onClick={save}
                data-testid="save-gab"
                disabled={!form.name.trim()}
                className="bg-foreground text-white text-xs px-4 py-2 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              >
                <Plus className="w-3 h-3" weight="bold" />Crear gabinete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, icon: Icon, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-widest font-semibold text-muted mb-1 inline-flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" weight="bold" />}
        {label}{required && <span className="text-status-error-fg">*</span>}
      </label>
      {children}
    </div>
  );
}
